import { DateRangeValue, Identifiable } from '@custis/common';
import { ColumnInstance, TableHeaderProps } from 'react-table';
import React, { Key, useMemo, useState } from 'react';
import { GridFilterProps } from '../../filters';
import classNames from 'classnames';
import s from './Header.module.less';
import { Popover } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, ExclamationOutlined, SearchOutlined } from '@ant-design/icons';
import { IColumnClue, TableRenderComponents, TTableColumn } from '../../models';
import { CellAlign } from '../cell-align/CellAlign';

type OnColumnHeaderClickCallback<T extends Identifiable> = (
  e: React.MouseEvent<Element, MouseEvent>,
  column: ColumnInstance<T>,
) => void;

type THeaderProps = TableHeaderProps & { onClick?(e: React.MouseEvent<Element, MouseEvent>): void };

type HeaderColumn<T extends Identifiable, TFilter, TSorting> = TTableColumn<T, TFilter, TSorting> & ColumnInstance<T>;

export function HeaderCell<T extends Identifiable, TFilter, TSorting>(props: {
  column: HeaderColumn<T, TFilter, TSorting>;
  onClick?: OnColumnHeaderClickCallback<T>;
}) {
  const [filterVisible, setFilterVisible] = useState(false);
  const column = useMemo(() => props.column, [props.column]);
  const cellProps: THeaderProps = column.getHeaderProps(column.getSortByToggleProps());

  cellProps.style = {
    ...cellProps.style,
    display: 'inline-flex',
  };
  const onClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    if (props.onClick) {
      props.onClick(e, column);
    }
    if (cellProps.onClick) {
      cellProps.onClick(e);
    }
  };
  const cleanedCellProps = { ...cellProps };
  cleanedCellProps.onClick = void 0;

  const filterNode = useMemo(() => {
    return column.Filter;
  }, [column.Filter]);

  const p = useMemo<GridFilterProps<Key[]>>(
    () => ({
      visible: filterVisible,
      // TODO если надо управлять видимостью изнутри - добавить передачу setFilterVisible
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: column.filterValue,
      onConfirm: (value: Key[] | DateRangeValue | undefined) => {
        column.setFilter(Array.isArray(value) ? (value?.length === 0 ? undefined : value) : value);
        setFilterVisible(false);
      },
      onReset: () => {
        column.setFilter(undefined);
        setFilterVisible(false);
      },
    }),
    [filterVisible, column.filterValue],
  );

  const popoverContent = filterNode ? column.render(TableRenderComponents.Filter, p) : '';

  const filteredIconClass = useMemo(
    () =>
      classNames({
        [s.FilteredIcon]: !!column.filterValue,
      }),
    [column.filterValue],
  );

  // Тут задаём вид фильтра, как он должен выглядеть в гриде
  // Если потребуется, вынести это в меню колонки
  const filterZone = column.disableFilters ? null : (
    <div className={s.Filter} title={'Отфильтровать столбец'}>
      <Popover content={popoverContent} trigger="click" visible={filterVisible} onVisibleChange={setFilterVisible}>
        <SearchOutlined className={filteredIconClass} /> {/* TODO Добавить выделение цветом */}
      </Popover>
    </div>
  );

  const sortingZone = column.disableSortBy ? null : (
    <div className={s.Sorter} onClick={onClick}>
      {column.isSorted ? column.isSortedDesc ? <CaretDownOutlined /> : <CaretUpOutlined /> : ''}
    </div>
  );

  const resizerZone = column.disableResizing ? null : (
    <div {...column.getResizerProps()} className={`${s.Resizer} ${column.isResizing ? 'isResizing' : ''}`} />
  );

  const renderClue = (clue: IColumnClue) => (
    <div className={s.Clue}>
      <Popover content={clue.content} trigger={'hover'}>
        {clue.icon || <ExclamationOutlined className={s.ClueIcon} />}
      </Popover>
    </div>
  );

  return (
    <div
      {...cleanedCellProps}
      key={column.id}
      className={classNames(s.GridHeaderCell, 'GridHeaderCell')}
      title={undefined}
    >
      {column.clue && column.clue.position !== 'right' && renderClue(column.clue)}
      <CellAlign
        type={(column as TTableColumn<T, TFilter, TSorting>).type}
        className={classNames(s.GridHeaderCellContent, s.GridDrag, 'GridDrag')}
        onClick={onClick}
      >
        {column.render(TableRenderComponents.Header)}
      </CellAlign>
      {column.clue && column.clue?.position === 'right' && renderClue(column.clue)}
      <div className={classNames(s.Addons, 'Addons')}>
        {sortingZone}
        {filterZone}
        {resizerZone}
      </div>
      <div className={s.Divider} />
    </div>
  );
}
